// Fetching and hydrating frozen puzzle snapshots.
//
// Puzzles used to be generated in the browser from the date, which meant the
// 388KB word list shipped in the bundle and every past date silently changed
// whenever the generator did. They are now produced offline and stored, and
// the client just renders what it is given.

const PAYLOAD_VERSION = 1;

// Snapshots are immutable, so a fetched puzzle is cached for the session.
// handleClear and (later) archive back-navigation reuse this rather than
// refetching.
const cache = new Map();

class PuzzleError extends Error {
  constructor(message, kind) {
    super(message);
    this.name = 'PuzzleError';
    this.kind = kind;
  }
}

/**
 * Check the payload before anything renders.
 *
 * These are all conditions that would otherwise produce a puzzle that looks
 * fine but cannot be completed, with no obvious symptom.
 */
const validatePayload = (payload) => {
  if (!payload || typeof payload !== 'object') throw new PuzzleError('empty payload', 'malformed');

  if (payload.payloadVersion !== PAYLOAD_VERSION) {
    throw new PuzzleError(
      `unsupported payload version ${payload.payloadVersion}`,
      'version'
    );
  }

  const { gridSize, solution, locked, words, tiles } = payload;

  if (!Number.isInteger(gridSize) || gridSize < 3) throw new PuzzleError('bad gridSize', 'malformed');
  if (!Array.isArray(solution) || solution.length !== gridSize) {
    throw new PuzzleError('solution does not match gridSize', 'malformed');
  }
  if (solution.some((row) => typeof row !== 'string' || row.length !== gridSize)) {
    throw new PuzzleError('malformed solution row', 'malformed');
  }
  if (!Array.isArray(words) || words.length === 0) throw new PuzzleError('no words', 'malformed');
  if (!Array.isArray(tiles) || tiles.length === 0) throw new PuzzleError('no tiles', 'malformed');
  if (!Array.isArray(locked)) throw new PuzzleError('no locked array', 'malformed');

  // -1 is the historical sentinel for a revealed cell; a real tile must never
  // collide with it.
  if (tiles.some((t) => !Number.isInteger(t.id) || t.id < 0)) {
    throw new PuzzleError('tile ids must be non-negative', 'malformed');
  }

  // Each tile carries the span it was cut from. Reveals place the real tile
  // rather than painting a synthetic cell, so without this the bank and the
  // board fall out of step and the puzzle becomes unwinnable.
  if (tiles.some((t) => !Array.isArray(t.cells) || t.cells.length !== t.letters.length)) {
    throw new PuzzleError('tiles are missing their cell spans', 'malformed');
  }

  // Answers must agree with the grid, or the completion check never fires.
  words.forEach((w) => {
    for (let i = 0; i < w.word.length; i++) {
      const row = w.direction === 'across' ? w.startRow : w.startRow + i;
      const col = w.direction === 'across' ? w.startCol + i : w.startCol;
      if (solution[row]?.[col] !== w.word[i]) {
        throw new PuzzleError(`${w.word} disagrees with the grid`, 'inconsistent');
      }
    }
  });

  return payload;
};

/**
 * Snapshot -> the shapes the app already uses.
 *
 * Field names match what the components expected from the old client-side
 * generator (notably `definition` rather than the payload's `clue`), so this
 * is the only place that has to know about the payload format.
 */
export const hydrate = (payload) => {
  const { gridSize, solution, locked, words, tiles } = payload;

  const lockedByKey = new Map(locked.map((l) => [`${l.row},${l.col}`, l]));

  const grid = solution.map((row, r) =>
    row.split('').map((char, c) => {
      const isBlocked = char === '.';
      const lockedCell = lockedByKey.get(`${r},${c}`);
      return {
        letter: lockedCell ? lockedCell.letter : '',
        isBlocked,
        isLocked: Boolean(lockedCell),
        tileId: null,
        cellDisplay: isBlocked ? 'blocked' : lockedCell ? 'locked' : 'open',
      };
    })
  );

  // What the completion check compares against.
  const solutionGrid = solution.map((row) =>
    row.split('').map((char) => ({
      letter: char === '.' ? '' : char,
      isBlocked: char === '.',
    }))
  );

  const placedWords = words.map((w) => ({
    word: w.word,
    definition: w.clue,
    direction: w.direction,
    startRow: w.startRow,
    startCol: w.startCol,
  }));

  return {
    gridSize,
    grid,
    solutionGrid,
    placedWords,
    // `letters` is copied too, not just the tile object. The hydrated snapshot
    // is cached for the session and handleClear rehydrates from it, so a shared
    // inner array would let any mutation corrupt every later reset.
    tiles: tiles.map((t) => ({
      ...t,
      letters: [...t.letters],
      cells: t.cells.map((c) => ({ ...c })),
    })),
    lockedPositions: locked,
  };
};

/** Fetch a snapshot. Omit puzzleId for today. */
export const fetchPuzzle = async (puzzleId = null, { signal } = {}) => {
  const key = puzzleId ? String(puzzleId) : 'today';
  if (cache.has(key)) return cache.get(key);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const query = puzzleId ? `?puzzle_id=${encodeURIComponent(puzzleId)}` : '';

  let response;
  try {
    response = await fetch(`${backendUrl}/api/getPuzzle${query}`, { signal });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new PuzzleError('could not reach the server', 'network');
  }

  if (!response.ok) {
    const kind =
      response.status === 403 ? 'not_yet_published'
        : response.status === 404 ? 'not_found'
          : 'server';
    throw new PuzzleError(`server returned ${response.status}`, kind);
  }

  const body = await response.json();
  const hydrated = hydrate(validatePayload(body.payload));

  const result = {
    puzzleId: body.puzzle_id,
    date: body.date,
    isToday: body.is_today,
    theme: body.theme,
    ...hydrated,
  };

  cache.set(key, result);
  // Also cache under the resolved id so "today" and its explicit id share one entry.
  if (body.puzzle_id) cache.set(String(body.puzzle_id), result);

  return result;
};

export { PuzzleError };
